"use client";

import { useEffect, useState } from "react";

interface AutoRetryProps {
  duration?: number;
  onRetry?: () => void;
  className?: string;
  theme?: "light" | "dark" | "admin" | "brand" | "amber";
}

export function AutoRetry({
  duration = 5,
  onRetry = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  },
  className = "",
  theme = "light",
}: AutoRetryProps) {
  const [countdown, setCountdown] = useState(duration);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onRetry();
          return 0;
        }
        return prev - 1;
      });
      setProgress((prev) => Math.min(prev + 100 / duration, 100));
    }, 1000);

    return () => clearInterval(timer);
  }, [duration, onRetry]);

  const getThemeStyles = () => {
    switch (theme) {
      case "amber":
        return {
          bg: "rgba(255,255,255,0.06)",
          fill: "linear-gradient(90deg, #f59e0b, #d97706)",
          text: "#94a3b8",
        };
      case "dark":
      case "brand":
        return {
          bg: "rgba(255,255,255,0.06)",
          fill: "linear-gradient(90deg, #e879a0, #f0abfc)",
          text: "#64748b",
        };
      case "admin":
        return {
          bg: "rgba(255,255,255,0.1)",
          fill: "linear-gradient(90deg, #4c6ef5, #7048e8)",
          text: "var(--admin-text-secondary, #9ca3af)",
        };
      case "light":
      default:
        return {
          bg: "rgba(0,0,0,0.06)",
          fill: "linear-gradient(90deg, #3b82f6, #6366f1)",
          text: "#6b7280",
        };
    }
  };

  const styles = getThemeStyles();

  return (
    <div style={{ marginBottom: "20px" }} className={className}>
      <div
        style={{
          height: "4px",
          background: styles.bg,
          borderRadius: "4px",
          overflow: "hidden",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progress}%`,
            background: styles.fill,
            borderRadius: "4px",
            transition: "width 1s linear",
          }}
        />
      </div>
      <p
        style={{
          fontSize: "12px",
          color: styles.text,
          textAlign: "center",
          fontWeight: 500,
        }}
      >
        Auto-retrying in {countdown}s
      </p>
    </div>
  );
}
